import { Inject, Injectable, Optional } from '@angular/core';
import mixpanel, { Config } from 'mixpanel-browser';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { AnalyticsBaseService } from './analytics-base.service';
import { AnalyticsEvent } from '../../models/analytics/analytics-event.model';
import { AdminService } from '../admin/admin.service';
import {
  DEFAULT_MIXPANEL_API_HOST,
  MIXPANEL_ANALYTICS_CONFIG,
  MixpanelAnalyticsConfig,
} from './mixpanel-analytics-config';

/** Shared Mixpanel implementation of AnalyticsBaseService; config via MIXPANEL_ANALYTICS_CONFIG. */
@Injectable({ providedIn: 'root' })
export class MixpanelService implements AnalyticsBaseService {
  private readonly APP_SESSION_ID_KEY = 'user_session_id';
  private readonly KC_SESSION_KEY = 'current_keycloak_sid';
  private readonly userRolesCache = new Map<string, Promise<string[]>>();

  constructor(
    private readonly keycloakService: KeycloakService,
    @Optional()
    @Inject(MIXPANEL_ANALYTICS_CONFIG)
    private readonly config: MixpanelAnalyticsConfig | null,
    @Optional() private readonly adminService: AdminService | null = null
  ) {
    if (this.config?.isActive) {
      this.initialize({
        token: this.config.token,
        options: {
          ignore_dnt: true,
          api_host: this.config.apiHost ?? DEFAULT_MIXPANEL_API_HOST,
        },
      });
    }
  }

  initialize(config: { token: string; options?: Partial<Config> }): void {
    try {
      mixpanel.init(config.token, config.options);
    } catch (error) {
      console.error('Error initializing Mixpanel:', error);
    }
  }

  async trackEvent(
    eventData: Pick<AnalyticsEvent, 'eventType'> & Partial<AnalyticsEvent>
  ): Promise<void> {
    try {
      if (!this.config?.isActive) return;

      if ((this.config.excludedEvents ?? []).includes(eventData.eventType)) {
        return;
      }

      const isAuthenticated = await this.keycloakService.isLoggedIn();
      const user = isAuthenticated
        ? await this.keycloakService.loadUserProfile()
        : null;
      const roles =
        isAuthenticated && user?.id && this.adminService
          ? await this.getUserProfileRoles(user.id)
          : [];

      const keycloakSid =
        this.keycloakService.getKeycloakInstance()?.idTokenParsed?.['sid'];
      this.initSession((keycloakSid as string) || 'anonymous');
      const sessionId = localStorage.getItem(this.APP_SESSION_ID_KEY);

      const language =
        this.config.getLocale?.() || this.config.defaultLocale || 'rw-RW';

      const standardProperties = {
        user_id: user?.id,
        user_role: roles,
        session_id: sessionId,
        language,
        timestamp: new Date().toISOString(),
      };

      const { eventType, metadata, errorMessage, ...eventProperties } =
        eventData;

      mixpanel.track(eventType, {
        ...eventProperties,
        ...metadata,
        ...standardProperties,
        error_Message: eventData.error_Message ?? errorMessage ?? null,
      });

      if (isAuthenticated && user) {
        mixpanel.people.set({
          $email: user.email,
          $name: `${user.firstName} ${user.lastName}`,
          user_role: standardProperties.user_role,
          language: standardProperties.language,
        });
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  private getUserProfileRoles(userId: string): Promise<string[]> {
    const cachedRoles = this.userRolesCache.get(userId);
    if (cachedRoles) return cachedRoles;

    const rolesRequest = firstValueFrom(this.adminService!.getUserById(userId))
      .then(response =>
        (response.data?.userRoles ?? [])
          .map(role => role.name)
          .filter(
            (role): role is string =>
              typeof role === 'string' && role.startsWith('ROLE_')
          )
          .map(role => role.slice('ROLE_'.length))
      )
      .catch(() => []);

    this.userRolesCache.set(userId, rolesRequest);
    return rolesRequest;
  }

  /** Registers a stable session id per Keycloak session (new id when the sid changes). */
  initSession(keycloakSid: string): void {
    const storedKcSid = localStorage.getItem(this.KC_SESSION_KEY);
    let appSessionId = localStorage.getItem(this.APP_SESSION_ID_KEY);

    if (keycloakSid !== storedKcSid || !appSessionId) {
      appSessionId = crypto.randomUUID();
      localStorage.setItem(this.APP_SESSION_ID_KEY, appSessionId);
      localStorage.setItem(this.KC_SESSION_KEY, keycloakSid);
    }

    mixpanel.register({
      session_id: appSessionId,
      user_session_id: appSessionId,
    });
  }

  setUser(userId: string, userProperties?: Record<string, any>): void {
    mixpanel.identify(userId);
    if (userProperties) {
      mixpanel.people.set(userProperties);
    }
  }

  reset(): void {
    const mp = (globalThis as any).mixpanel;
    if (mp && typeof mp.reset === 'function') {
      mp.reset();
    }
    localStorage.removeItem(this.APP_SESSION_ID_KEY);
    localStorage.removeItem(this.KC_SESSION_KEY);
  }
}
